import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader } from '@/components/ui/loader';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, LogOut, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, isLoading, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  if (isLoading || !user) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Loader />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">{t('profile.title')}</h1>
            <p className="text-muted-foreground">{t('profile.subtitle')}</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="gap-2">
            <LogOut className="h-4 w-4" />
            {t('profile.logout')}
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:max-w-4xl">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('profile.userInformation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarFallback>
                    {user.name
                      ?.split(' ')
                      .map(n => n[0])
                      .join('') || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <h3 className="font-semibold">{user.name}</h3>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="grid grid-cols-3 items-center gap-2 text-sm">
                  <span className="font-medium">{t('profile.username')}</span>
                  <span className="col-span-2 text-muted-foreground">{user.username}</span>
                </div>
                <div className="grid grid-cols-3 items-center gap-2 text-sm">
                  <span className="font-medium">{t('profile.userId')}</span>
                  <span className="col-span-2 text-muted-foreground font-mono text-xs">
                    {user.user_id}
                  </span>
                </div>
                <div className="grid grid-cols-3 items-center gap-2 text-sm">
                  <span className="font-medium">{t('profile.organization')}</span>
                  <span className="col-span-2 text-muted-foreground font-mono text-xs">
                    {user.org_id}
                  </span>
                </div>
                <div className="grid grid-cols-3 items-center gap-2 text-sm">
                  <span className="font-medium">{t('profile.userLanguage')}</span>
                  <span className="col-span-2 text-muted-foreground">{user.language}</span>
                </div>
                <div className="grid grid-cols-3 items-center gap-2 text-sm">
                  <span className="font-medium">{t('profile.userTimezone')}</span>
                  <span className="col-span-2 text-muted-foreground">{user.timezone}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                {t('profile.authenticationStatus')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('profile.status')}</span>
                  <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                    {t('profile.authenticated')}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
